import React, { useEffect, useRef, useState } from "react";

import {
  getPorkPigletCapacityCheck,
  inputStateForValue,
  isNullOrUndefined,
  isValidNonnegativeNumber,
  isValidVVVOIdentifier,
  queryIsLoading,
  useGetCarbonProgramByFarmId,
  useGetFootprintingPigletFootprint,
  useGetFootprintingPorkFootprint,
  useGetFootprintingPorkHerdDetails,
  useKeyedFormList,
  useMutateFootprintingPigletPrimaryData,
  useMutateFootprintingPorkPigletSourcing,
  useNavigate,
  usePracticeManagementPorkParameters,
  useTranslation,
} from "utils";

import { COUNTRY_CODE, HARD_INPUT_STATES, INPUT_STATE, TOO_HIGH_INPUT_STATES, TOO_LOW_INPUT_STATES } from "consts";

import { ENUM_PORK_PIGLET_SOURCING_TYPE, FootprintingPorkPigletSourcingPayload, PORK_PIGLET_SOURCING_TYPE } from "models";

import {
  AlertBanner,
  Button,
  Card,
  Col,
  EmptyStateCard,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  Input,
  InputNumber,
  KlimRadioGroup,
  PageSpin,
  Row,
  SanityCheck,
  Typography,
} from "components";

type SourceFarmEntry = {
  id?: string;
  vvvoIdentifier: string | null;
  sourceFarmName: string | null;
  quantityPurchased: number | null;
  averageWeightKg: number | null;
};

const emptySourceFarm: SourceFarmEntry = {
  sourceFarmName: null,
  vvvoIdentifier: null,
  quantityPurchased: null,
  averageWeightKg: null,
};

type PigletSourcingFormProps = {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
  onNavigateToStep?: (step: number) => void;
};

const PorkPigletSourcingForm = ({ primaryDataId, readOnly, farmId, year, onNavigateToStep }: PigletSourcingFormProps) => {
  const { t } = useTranslation("footprintingPork");
  const navigate = useNavigate();
  const formRef = useRef<FormInstance>(null);

  const [sourcingType, setSourcingType] = useState<ENUM_PORK_PIGLET_SOURCING_TYPE | null>(null);

  const { data: carbonProgram } = useGetCarbonProgramByFarmId(farmId);
  const porkFootprintQuery = useGetFootprintingPorkFootprint(farmId, year);
  const { data: herdDetails } = useGetFootprintingPorkHerdDetails(primaryDataId ?? "", { enabled: !!primaryDataId });
  const porkPracticeQuery = usePracticeManagementPorkParameters(carbonProgram?.country?.toLowerCase() as PracticeManagement.CountryCode);
  const isLoading = [porkFootprintQuery, porkPracticeQuery].some(queryIsLoading);
  const keyedSourceFarms = useKeyedFormList<SourceFarmEntry>(formRef, { isReady: !isLoading });
  const pigletFootprintQuery = useGetFootprintingPigletFootprint(farmId, year);
  const { mutateAsync: createPigletAssessment } = useMutateFootprintingPigletPrimaryData(farmId, year, { farmId, year });

  const handleOpenPigletAssessment = async () => {
    if (!pigletFootprintQuery.data?.footprint?.id) {
      await createPigletAssessment({});
    }
    navigate("/footprinting/form", { state: { year, formType: "piglet" } });
  };

  const payload: FootprintingPorkPigletSourcingPayload = {
    sourcingType: sourcingType ?? PORK_PIGLET_SOURCING_TYPE.SELF_PRODUCED,
    sourceFarms:
      sourcingType === PORK_PIGLET_SOURCING_TYPE.PURCHASED
        ? keyedSourceFarms.payload.filter((sf) => sf.sourceFarmName !== null).map((sf) => ({ ...sf, vvvoIdentifier: sf.vvvoIdentifier?.toLowerCase() ?? null }))
        : [],
  };

  const { mutate: mutatePigletSourcing } = useMutateFootprintingPorkPigletSourcing(farmId, year, payload, primaryDataId ?? "");

  const totalPurchased = keyedSourceFarms.entries.reduce((sum, sf) => sum + (sf.quantityPurchased ?? 0), 0);
  const capacityCheck = getPorkPigletCapacityCheck(herdDetails?.averageHerdSize, herdDetails?.rearingPeriodDays, totalPurchased);

  // Matched on the too-high/too-low groups rather than a single state, so the banners stay correct
  // regardless of whether the capacity bounds treat an over-purchase as a hard or a soft breach.
  const capacityState = capacityCheck.state;
  const isOverCapacity = capacityState != null && TOO_HIGH_INPUT_STATES.includes(capacityState);
  const isUnderCapacity = capacityState != null && TOO_LOW_INPUT_STATES.includes(capacityState);
  const isWithinCapacity = capacityState === INPUT_STATE.expected;

  useEffect(
    () => () => {
      if (!readOnly && !isLoading && primaryDataId && sourcingType !== null) {
        mutatePigletSourcing();
      }
    },
    [isLoading, mutatePigletSourcing, readOnly, primaryDataId, sourcingType]
  );

  useEffect(() => {
    const pigletSourcing = porkFootprintQuery.data?.footprint?.pigletSourcing;
    if (!pigletSourcing) return;

    setSourcingType(pigletSourcing.sourcingType);

    const loaded = pigletSourcing.sourceFarms.map((sf) => ({
      id: sf.id,
      sourceFarmName: sf.sourceFarmName,
      vvvoIdentifier: sf.vvvoIdentifier ?? null,
      quantityPurchased: sf.quantityPurchased,
      averageWeightKg: sf.averageWeightKg,
    }));
    keyedSourceFarms.actions.hydrate(loaded.length > 0 ? loaded : [emptySourceFarm]);

    // A saved "purchased" answer is checked straight away; switching to "purchased" later starts with a neutral blank farm
    if (pigletSourcing.sourcingType === PORK_PIGLET_SOURCING_TYPE.PURCHASED) keyedSourceFarms.actions.requestRehydrationValidation();
  }, [porkFootprintQuery.data, keyedSourceFarms.actions]);

  useEffect(() => {
    if (sourcingType === PORK_PIGLET_SOURCING_TYPE.SELF_PRODUCED && !readOnly && primaryDataId) {
      mutatePigletSourcing();
    }
  }, [sourcingType, primaryDataId, readOnly, mutatePigletSourcing]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Card border dataTestId="footprinting-pork-piglet-sourcing-form" className="padding-200">
      <Form ref={formRef} preserve={false}>
        <Flex vertical gap={24}>
          <Flex align="center" gap={16} justify="space-between">
            <Typography variant="paragraph-m" style={{ maxWidth: "50%" }}>
              {t("porkPigletSourcingForm.sourcingTypeQuestion")}
              <span className="required-asterisk">*</span>
            </Typography>
            <KlimRadioGroup
              required
              name="sourcingType"
              dataTestId="footprinting-pork-piglet-sourcing-radio"
              disabled={readOnly}
              vertical={false}
              type="tertiary"
              size="large"
              onChange={({ target }) => {
                setSourcingType(target.value);
                if (target.value === PORK_PIGLET_SOURCING_TYPE.PURCHASED && keyedSourceFarms.entries.length === 0) {
                  keyedSourceFarms.actions.add(emptySourceFarm);
                }
              }}
              value={sourcingType}
              defaultValue={sourcingType}
              optionType="button"
              options={[PORK_PIGLET_SOURCING_TYPE.SELF_PRODUCED, PORK_PIGLET_SOURCING_TYPE.PURCHASED].map((type) => ({
                label: t(`porkPigletSourcingForm.sourcingTypeOptions.${type}`),
                value: type,
              }))}
            />
          </Flex>

          {sourcingType === PORK_PIGLET_SOURCING_TYPE.SELF_PRODUCED && (
            <EmptyStateCard
              dataTestId="piglet-sourcing-self-produced-empty-state"
              iconName="tabler/info-circle"
              title={t("porkPigletSourcingForm.selfProducedTitle")}
              description={t("porkPigletSourcingForm.selfProducedInfo")}
              buttonText={t("porkPigletSourcingForm.openPigletRaisingAssessment")}
              onButtonClick={handleOpenPigletAssessment}
              buttonType="text"
            />
          )}

          {sourcingType === PORK_PIGLET_SOURCING_TYPE.PURCHASED && (
            <>
              {keyedSourceFarms.entries.map((sourceFarm, index) => {
                const updateSourceFarm = (updates: Partial<SourceFarmEntry>) => keyedSourceFarms.actions.update(sourceFarm.clientKey, updates);

                return (
                  <Flex key={sourceFarm.clientKey} vertical gap={24}>
                    <Flex justify="space-between" align="center">
                      <Typography variant="h5">{t("porkPigletSourcingForm.sourceFarmNumber", { count: index + 1 })}</Typography>
                      {keyedSourceFarms.entries.length > 1 && (
                        <IconButton
                          type="tertiary"
                          disabled={readOnly}
                          dataTestId={`delete-source-farm-${index}`}
                          size="small"
                          iconName="tabler/trash"
                          onClick={() => keyedSourceFarms.actions.remove(sourceFarm.clientKey)}
                        />
                      )}
                    </Flex>

                    <Input
                      className="floating-label"
                      disabled={readOnly}
                      name={`source-farm-sourceFarmName-${sourceFarm.clientKey}`}
                      dataTestId={`source-farm-sourceFarmName-${index}`}
                      label={t("porkPigletSourcingForm.question.sourceFarmLabel")}
                      value={sourceFarm.sourceFarmName ?? ""}
                      initialValue={sourceFarm.sourceFarmName ?? ""}
                      onChange={(e) => updateSourceFarm({ sourceFarmName: e.target.value ?? null })}
                      rules={[{ required: true, type: "string" }]}
                    />

                    {carbonProgram?.country === COUNTRY_CODE.GERMANY ? (
                      <Input
                        className="hide-error-message"
                        disabled={readOnly}
                        name={`vvvoIdentifier-${sourceFarm.clientKey}`}
                        dataTestId={`footprinting-pork-vvvo-identifier-input-${index}`}
                        label={t("porkPigletSourcingForm.question.vvvoIdentifierLabel")}
                        value={sourceFarm.vvvoIdentifier ?? undefined}
                        initialValue={sourceFarm.vvvoIdentifier ?? undefined}
                        // Prevent setting empty string
                        onChange={(e) => updateSourceFarm({ vvvoIdentifier: e.target.value ? e.target.value : null })}
                        validateFirst
                        rules={[
                          { required: true, type: "string" },
                          { validator: (_: unknown, v: string) => (isValidVVVOIdentifier(v) ? Promise.resolve() : Promise.reject()) },
                        ]}
                        guidanceText={t("porkPigletSourcingForm.question.vvvoIdentifierGuidance")}
                      />
                    ) : null}

                    <Row gutter={0}>
                      <Col xs={24} sm={12} className="padding-right-50">
                        <InputNumber
                          disabled={readOnly}
                          name={`quantityPurchased-${sourceFarm.clientKey}`}
                          dataTestId={`footprinting-pork-quantity-purchased-input-${index}`}
                          label={t("porkPigletSourcingForm.question.quantityPurchasedLabel")}
                          suffix={t("porkPigletSourcingForm.question.quantityPurchasedSuffix")}
                          value={sourceFarm.quantityPurchased}
                          initialValue={sourceFarm.quantityPurchased}
                          onChange={(v) => updateSourceFarm({ quantityPurchased: v })}
                          validateFirst
                          rules={[
                            { required: true, type: "number", message: t("porkPigletSourcingForm.error.validQuantity") },
                            {
                              validator: (_: unknown, v: number) => {
                                if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                                  return Promise.reject(new Error(t("porkPigletSourcingForm.error.validQuantity")));
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        />
                      </Col>
                      <Col xs={24} sm={12} className="padding-left-50">
                        <Form.Item
                          validateStatus={
                            sourceFarm.averageWeightKg
                              ? HARD_INPUT_STATES.includes(inputStateForValue(sourceFarm.averageWeightKg, porkPracticeQuery.data?.pigletWeightAtPurchaseKgs))
                                ? "error"
                                : "success"
                              : "success"
                          }
                        >
                          <InputNumber
                            disabled={readOnly}
                            name={`averageWeightKg-${sourceFarm.clientKey}`}
                            dataTestId={`footprinting-pork-average-weight-input-${index}`}
                            label={t("porkPigletSourcingForm.question.averageWeightLabel")}
                            suffix={t("porkPigletSourcingForm.question.averageWeightSuffix")}
                            value={sourceFarm.averageWeightKg}
                            initialValue={sourceFarm.averageWeightKg}
                            onChange={(v) => updateSourceFarm({ averageWeightKg: v })}
                            validateFirst
                            guidanceText={porkPracticeQuery.data?.pigletWeightAtPurchaseKgs}
                            rules={[
                              {
                                required: true,
                                type: "number",
                                message: t("porkPigletSourcingForm.error.validWeight", {
                                  min: porkPracticeQuery.data?.pigletWeightAtPurchaseKgs?.[1],
                                  max: porkPracticeQuery.data?.pigletWeightAtPurchaseKgs?.[2],
                                }),
                              },
                            ]}
                          />
                        </Form.Item>
                        {sourceFarm.averageWeightKg ? (
                          <SanityCheck inputValue={sourceFarm.averageWeightKg} inputRange={porkPracticeQuery.data?.pigletWeightAtPurchaseKgs} />
                        ) : null}
                      </Col>
                    </Row>
                  </Flex>
                );
              })}

              <Button type="text" dataTestId="add-source-farm-button" disabled={readOnly} onClick={() => keyedSourceFarms.actions.add(emptySourceFarm)}>
                <Icon iconName="klim/plus" className="icon inline" /> {t("porkPigletSourcingForm.addSourceFarm")}
              </Button>

              <Card border className="dairy-display-information-card padding-100 flex-center vertical">
                <Flex vertical>
                  <Typography variant="paragraph-s" color="grey-600" className="margin-bottom-25">
                    {t("porkPigletSourcingForm.totalAmountPurchasedLabel")}
                  </Typography>
                  <Typography variant="h5" className="center">
                    {capacityCheck.capacity != null
                      ? `${totalPurchased}/${capacityCheck.capacity} ${t("porkPigletSourcingForm.question.quantityPurchasedSuffix")}`
                      : `${totalPurchased} ${t("porkPigletSourcingForm.question.quantityPurchasedSuffix")}`}
                  </Typography>
                </Flex>
              </Card>

              {capacityCheck.capacity != null && isOverCapacity && (
                <AlertBanner
                  type="error"
                  iconName="tabler/alert-circle"
                  iconSize={24}
                  iconAlign="start"
                  isTitleBold
                  headingSize="paragraph-m"
                  title={t("porkPigletSourcingForm.validationBanner.tooHighTitle")}
                  description={t("porkPigletSourcingForm.validationBanner.tooHighDescription", { total: totalPurchased, capacity: capacityCheck.capacity })}
                  link
                  linkText={t("porkPigletSourcingForm.validationBanner.editHerdSizeLink")}
                  linkType="secondary-link"
                  onClick={() => onNavigateToStep?.(2)}
                />
              )}
              {capacityCheck.capacity != null && isUnderCapacity && (
                <AlertBanner
                  type="warning"
                  iconName="tabler/alert-circle"
                  iconSize={24}
                  iconAlign="start"
                  isTitleBold
                  headingSize="paragraph-m"
                  title={t("porkPigletSourcingForm.validationBanner.tooLowTitle")}
                  description={t("porkPigletSourcingForm.validationBanner.tooLowDescription", {
                    total: totalPurchased,
                    capacity: capacityCheck.capacity,
                    percentage: Math.round(((capacityCheck.capacity - totalPurchased) / capacityCheck.capacity) * 100),
                  })}
                  link
                  linkText={t("porkPigletSourcingForm.validationBanner.editHerdSizeLink")}
                  linkType="secondary-link"
                  onClick={() => onNavigateToStep?.(2)}
                />
              )}
              {capacityCheck.capacity != null && isWithinCapacity && (
                <AlertBanner
                  type="success"
                  iconName="tabler/circle-check"
                  iconSize={24}
                  iconAlign="start"
                  isTitleBold
                  headingSize="paragraph-m"
                  title={t("porkPigletSourcingForm.validationBanner.spotOnTitle")}
                  description={t("porkPigletSourcingForm.validationBanner.spotOnDescription", { total: totalPurchased, capacity: capacityCheck.capacity })}
                />
              )}
            </>
          )}
        </Flex>
      </Form>
    </Card>
  );
};

PorkPigletSourcingForm.Key = "pork.pigletSourcing";
export { PorkPigletSourcingForm };
